import type { PostInputMetaData } from '../../graphqlTypes'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import { PostTypeFacebookStory } from '~publish/legacy/post/constants'

import { getChannelVariantLinkAttachment } from '../../channelVariantToCreatePostInput'
import { mapLinkAttachment } from '../../mapAssets'
import type { MapperContext } from '../../types'
import { getFacebookAnnotations } from '../shared/annotations'
import { clearable } from '../shared/clearable'
import { getFirstComment } from '../shared/firstComment'
import { mapFacebookPostType } from '../shared/postTypes'
import type { ServiceMapperEntry } from '../types'

// Facebook: post type, first comment, page-mention annotations, and the link
// attachment, each tri-stated via `clearable`. A story never carries a first
// comment: the legacy save path strips a stale `comment_text` left over from
// switching a draft from Post to Story before saving, so this mapper applies
// the same strip regardless of what channelData still holds.
//
// `ctx.channelVariant` (when present) supplies post type, first comment, and
// the link attachment. Page-mention annotations still read the editor
// (`ctx.editor` when `composer-zustand-editor-state` is on, else
// `draft.editorState`) — rich Slate editor state, not something
// `ChannelVariant` models.
export function mapFacebook(
  draft: Draft,
  ctx: MapperContext,
): PostInputMetaData {
  const variant = ctx.channelVariant
  const isStoryPost = variant
    ? variant.updateType === PostTypeFacebookStory
    : draft.isFacebookStoryPost()
  const firstComment = isStoryPost
    ? undefined
    : variant
      ? variant.channelData?.facebook?.comment_text || undefined
      : getFirstComment(draft)
  const annotations = getFacebookAnnotations(draft, ctx)
  const linkAttachment = variant
    ? getChannelVariantLinkAttachment(variant)
    : mapLinkAttachment(draft)

  return {
    facebook: {
      type: mapFacebookPostType(
        variant ? variant.updateType : draft.updateType,
      ),
      firstComment: clearable(firstComment, ctx),
      annotations: clearable(annotations, ctx),
      linkAttachment: clearable(linkAttachment, ctx),
    },
  }
}

export const facebook: ServiceMapperEntry = {
  mapper: mapFacebook,
}
